import { useCallback, useMemo, useState } from "react";
import { formatShare } from "#app/widgets/definitions/shared/format-share";
import {
  Box,
  ColorSwatch,
  createColumnHelper,
  DataTable,
  DataTableCell,
  Icon,
  Text,
  WidgetDisclosureRow,
  WidgetEmpty,
  WidgetInteractive,
  WidgetListRow,
  useElementSize,
  useWidgetInstance,
  useWidgetSurface,
  type DataTableColumnDef,
  type DataTableGroupType,
  type WidgetRenderContext,
  WidgetListHeader,
  widgetListColumnsFor,
} from "@salestrak/ui";
import { ACTIVITY_ENTITY_TYPES, type ActivityEntityType } from "@salestrak/widgets";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import { LocalGroupProvider, useGroupStates } from "#app/lib/hooks/group-query-provider";
import { useWindowEmptyTitle } from "#app/widgets/dashboards/scope/use-window-copy";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import { useEntityOutcomePerformance, type OutcomeBreakdownRow, type OutcomeEntityRow } from "./use-outcome-performance";
import {
  type OutcomePerformanceConfig,
  type ToneKey,
  summaryTones,
  TONE_LABELS,
  TONE_COLORS,
  MEMBER_COLUMNS,
  type MemberColumn,
  EMPTY_OUTCOMES,
  breakdownReserveHeight,
} from "./vocabulary";
import { OutcomeFigure, MemberRowSkeleton } from "./members";

/** The kind glyph each entity row leads with. A row names a KIND of entity,
 *  not one entity, so it wears the kind's icon rather than an entity chip. */
const ENTITY_ROW_ICON: Record<ActivityEntityType, (typeof ENTITY_KIND_ICON)[keyof typeof ENTITY_KIND_ICON]> = {
  project: ENTITY_KIND_ICON.issue,
};

/** The zero row's caption: a kind whose rows exist but carry no Update yet.
 */
const NO_UPDATES_CAPTION = "No Updates";

function EntityRowLabel({ row, muted }: { row: OutcomeEntityRow; muted: boolean }) {
  return (
    <Box direction="row" align="center" gap="xs" fill="flex" shrink minWidth={0}>
      <Icon name={ENTITY_ROW_ICON[row.entityType]} size="sm" color={muted ? "faint" : "muted"} />
      <Text size="sm" weight="medium" variant={muted ? "muted" : undefined} truncate>
        {row.label}
      </Text>
    </Box>
  );
}

/** The figure cells an entity row and a member row share — the same columns,
 *  named once by the list header above either view. */
function EntityRowFigures({ row, columns }: { row: OutcomeEntityRow; columns: readonly MemberColumn[] }) {
  return (
    <>
      {columns.map((column) => (
        <Box key={column.key} width={column.width} shrink={false} align="end">
          <OutcomeFigure row={row} column={column.key} />
        </Box>
      ))}
    </>
  );
}

function EntityRow({
  row,
  columns,
  open,
  onToggle,
}: {
  row: OutcomeEntityRow;
  columns: readonly MemberColumn[];
  open: boolean;
  onToggle: () => void;
}) {
  if (row.total === 0) {
    return (
      <WidgetListRow>
        <EntityRowLabel row={row} muted />
        <Text size="xs" variant="muted">
          {NO_UPDATES_CAPTION}
        </Text>
      </WidgetListRow>
    );
  }
  return (
    <WidgetDisclosureRow expanded={open} label={`${row.label} Updates`} onToggle={onToggle}>
      <EntityRowLabel row={row} muted={false} />
      <EntityRowFigures row={row} columns={columns} />
    </WidgetDisclosureRow>
  );
}

/** The entity rows while their figures are in flight — one placeholder per
 *  kind, at the row's own geometry, so the list is the same height loading
 *  and loaded. */
function EntityRowsSkeleton({
  columns,
  openEntity,
}: {
  columns: readonly MemberColumn[];
  openEntity: ActivityEntityType | null;
}) {
  return (
    <Box direction="column">
      {ACTIVITY_ENTITY_TYPES.map((entityType) => (
        <MemberRowSkeleton key={entityType} columns={columns} selected={entityType === openEntity} />
      ))}
    </Box>
  );
}

const breakdownColumn = createColumnHelper<OutcomeBreakdownRow>();

/**
 * The outcomes behind one entity row — the subtable a row discloses, banded
 * by tone. A colored dot alone does not say which outcomes count as positive,
 * so the table sections the vocabulary the way the Outcomes settings page
 * does: a Positive band, a Neutral band, a Negative band, and — only when
 * Updates were posted without one — a No outcome band. Each band header
 * carries the tone's Updates subtotal and share, so the tone reading and the
 * per-outcome reading sit in one table. The three distribution bands always
 * render, matching the summary tiles: an empty Negative band is the good-news
 * reading, not a missing one.
 *
 * Each line is a press target that opens the widget's Data view on exactly
 * those Updates, so the card reads entity → tone → outcome → the Updates
 * themselves. The table sits in the island the activity panel uses at the
 * window-wide reserve ({@link breakdownReserveHeight}), compact, with ONE
 * column header hoisted above the first band — a header per band repeated the
 * same three words down the island and pushed the last band under the fold.
 */
function OutcomeBreakdownTable({
  row,
  reserveHeight,
  onSelect,
  fillHeight,
}: {
  row: OutcomeEntityRow;
  /** The window-wide reserve — see {@link breakdownReserveHeight}. */
  reserveHeight: number;
  onSelect: ((outcome: OutcomeBreakdownRow) => void) | undefined;
  fillHeight: boolean;
}) {
  const panelSurface = useWidgetSurface();
  const { states, registerState, getGroupProps } = useGroupStates<OutcomeBreakdownRow>();
  const tones = summaryTones(row.missing);
  const outcomesByTone = useMemo(() => {
    const map = new Map<ToneKey, OutcomeBreakdownRow[]>(tones.map((tone) => [tone, []]));
    for (const outcome of row.outcomes) map.get(outcome.tone)?.push(outcome);
    return map;
  }, [tones, row.outcomes]);
  const groups = useMemo<DataTableGroupType<OutcomeBreakdownRow>[]>(
    () =>
      tones.map((tone) => ({
        key: tone,
        label: TONE_LABELS[tone],
        totalCount: states.get(tone)?.total ?? outcomesByTone.get(tone)?.length ?? 0,
        ...getGroupProps(tone),
      })),
    [tones, getGroupProps, states, outcomesByTone],
  );
  // The band: the tone's dot and name, then its Updates subtotal and share in
  // the figure columns' own words. The row-count badge is hidden — how many
  // outcome words a tone has is not the reading; how many Updates landed in
  // it is.
  const renderGroupTitle = useCallback(
    (groupKey: string) => {
      const tone = groupKey as ToneKey;
      const count = row[tone];
      return (
        <Box direction="row" align="center" gap="sm" minWidth={0}>
          <Box direction="row" align="center" gap="xs" minWidth={0}>
            <ColorSwatch color={TONE_COLORS[tone]} size="xs" rounded="full" />
            <Text size="xs" weight="medium" truncate>
              {TONE_LABELS[tone]}
            </Text>
          </Box>
          <Text size="xs" variant="muted">
            {count.toLocaleString()} · {formatShare(count, row.total)}
          </Text>
        </Box>
      );
    },
    [row],
  );
  const columns = useMemo<DataTableColumnDef<OutcomeBreakdownRow>[]>(
    () => [
      breakdownColumn.accessor({
        accessorKey: "label",
        header: "Outcome",
        meta: { width: 320, minWidth: 200, maxWidthRatio: 0.6, truncate: true },
        cell: ({ row: line }) => {
          const { tone, label } = line.original;
          return (
            <DataTableCell textBrightness={tone === "missing" ? "subtle" : "high"}>
              <Text size="xs" truncate>
                {label}
              </Text>
            </DataTableCell>
          );
        },
      }),
      breakdownColumn.accessor({
        accessorKey: "count",
        header: "Updates",
        meta: { width: 120, minWidth: 90, maxWidth: 160 },
        cell: ({ row: line }) => <DataTableCell>{line.original.count.toLocaleString()}</DataTableCell>,
      }),
      breakdownColumn.accessor({
        accessorKey: "share",
        header: "Share",
        meta: { width: 120, minWidth: 90, maxWidth: 160 },
        cell: ({ row: line }) => (
          <DataTableCell textBrightness="subtle">{formatShare(line.original.share, 1)}</DataTableCell>
        ),
      }),
    ],
    [],
  );
  const loading = tones.some((tone) => states.get(tone)?.loading ?? true);

  return (
    <Box
      direction="column"
      fill={fillHeight ? "flex" : "width"}
      grow={fillHeight ? undefined : true}
      shrink={fillHeight ? undefined : false}
      overflow="hidden"
      height={fillHeight ? undefined : reserveHeight}
    >
      {tones.map((tone) => (
        <LocalGroupProvider<OutcomeBreakdownRow>
          key={tone}
          groupKey={tone}
          rows={outcomesByTone.get(tone) ?? EMPTY_OUTCOMES}
          contextKey={row.entityType}
          onStateChange={registerState}
        />
      ))}
      <DataTable
        surface={panelSurface}
        columns={columns}
        groups={groups}
        getRowId={(line) => line.outcomeId}
        loading={loading}
        // ONE column header for the whole table, bands under it, and it stays
        // put while the island scrolls.
        sharedHeader
        stickyHeader
        density="compact"
        fillColumnId="label"
        paddingX="md"
        paddingXAlign="content"
        groupTitle={renderGroupTitle}
        hideGroupCount
        onRowClick={onSelect ? (line) => onSelect(line.original) : undefined}
      />
    </Box>
  );
}

/**
 * The per-entity view: one row per entity kind, each disclosing its outcome
 * breakdown, each breakdown line opening the Data view on those Updates.
 * Which row is open is this viewer's reading of the card, so it is component
 * state, never config.
 */
export function EntityOutcomeRender(_context: WidgetRenderContext<OutcomePerformanceConfig>) {
  const { rows, total, loading, error } = useEntityOutcomePerformance();
  const emptyTitle = useWindowEmptyTitle("No Updates");
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();
  const [openEntity, setOpenEntity] = useState<ActivityEntityType | null>(null);
  const reserveHeight = useMemo(() => breakdownReserveHeight(rows), [rows]);
  // Columns follow the MEASURED body width, never the grid span.
  const { ref: bodyRef, size: bodySize } = useElementSize<HTMLDivElement>();
  const columns = widgetListColumnsFor(MEMBER_COLUMNS, bodySize.width);

  if (error) throw error;
  if (!loading && total === 0) {
    return (
      <WidgetEmpty
        title={emptyTitle}
      />
    );
  }

  const openRow = rows.find((row) => row.entityType === openEntity && row.total > 0) ?? null;
  const onSelectOutcome =
    drill && instance && openRow
      ? (outcome: OutcomeBreakdownRow) =>
          drill.openData(instance.instanceId, {
            kind: "outcomeActivity",
            entityTypes: [openRow.entityType],
            outcomeIds: [outcome.outcomeId],
            total: outcome.count,
            label: `${openRow.label} · ${outcome.label}`,
          })
      : undefined;

  return (
    // The card's height is the board's: the entity rows keep their size and
    // the disclosed table takes what remains, scrolling inside it.
    <Box ref={bodyRef} direction="column" grow minHeight={0}>
      <WidgetListHeader identity="Type" columns={columns} caretSlot />
      {loading ? (
        <EntityRowsSkeleton columns={columns} openEntity={openEntity} />
      ) : (
        <Box direction="column">
          {rows.map((row) => (
            <EntityRow
              key={row.entityType}
              row={row}
              columns={columns}
              open={openEntity === row.entityType}
              onToggle={() =>
                setOpenEntity((current) => (current === row.entityType ? null : row.entityType))
              }
            />
          ))}
        </Box>
      )}
      {openRow ? (
        <WidgetInteractive
          direction="column"
          marginTop="sm"
          // The body drops the card's end inset (`body: "bleed-bottom"`), so the
          // island that fills to the card's foot restores one of its own.
          marginBottom="md"
          grow
          minHeight={0}
          rounded="md"
          border
          overflow="hidden"
        >
          <OutcomeBreakdownTable
            row={openRow}
            reserveHeight={reserveHeight}
            onSelect={onSelectOutcome}
            fillHeight
          />
        </WidgetInteractive>
      ) : null}
    </Box>
  );
}
